import { useAppDispatch, useAppSelector } from 'src/hooks';
import { MultiSelect } from 'react-multi-select-component';

// adding App Insights hook import for telemetry logging
import { useAppInsights } from 'src/hooks/useAppInsights';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  LinearProgress,
  styled,
  TextareaAutosize,
  TextField,
  Typography,
  FormControlLabel,
  Backdrop, CircularProgress,
  Radio, RadioGroup
} from '@mui/material';
import { useFormik } from 'formik';
import moment from 'moment';
import { useEffect, useState } from 'react';
import {
  FormField2,
  getValidationSchema,
  IMultiSelectControlField2,
  ISelectOption
} from 'src/lib';
import { AnyObject } from 'yup/lib/types';
import { SelectComponent } from 'src/components/analytics';
import * as Yup from 'yup';
import { Close } from '@mui/icons-material';
import useMatomoFunction from 'src/hooks/use-matomo-function.hook';
import {
  createScenario,
  saveCopyOfScenario,
  updateScenario
} from '../../../scenarios';
import {
  getScenarioFilters,
  getScenarioNames,
  updateFilterFieldOptions
} from '../../../scenarios/store/scenario-slice';
import {
  IScenarioStaticFormFields,
  IDynamicFilterFormFields
} from '../../types/scenario-form.model';
import { Scenario } from '../../../scenarios/types/scenario.model';
import { MatomoEventTrackMapObject } from '../../../constants/constant';


const StyledFormHelperText = styled(FormHelperText)`
  font-size: 10px;
`;

const StyledTextField = styled(TextField)`
  input {
    font-size: 10px;
    background-color: #fcfcfc;
  }
`;

const StyledTextareaAutosize = styled(TextareaAutosize)`
  textarea {
    font-size: 10px;
    background-color: #fcfcfc;
  }
`;

interface ICreateScenarioDialogProps {
  currentScenario: Scenario;
  isOpen: boolean;
  onClose: (scenario: Scenario) => void;
  /* Flag for  scenario edit */
  isEditable: boolean;
  /* Flag for duplicate scenario creation */
  isDuplicate: boolean;
  // Optional property for loader text
  loaderText?: string
}

export default function CreateNewScenarioDialog({
  currentScenario,
  isOpen,
  onClose,
  isEditable = false,
  isDuplicate = false,
  loaderText
}: ICreateScenarioDialogProps) {
  const appDispatch = useAppDispatch();

  // adding App Insights hook initialization for telemetry logging
  const { trackEvent } = useAppInsights();
  const { callFunction } = useMatomoFunction();

  // setting schema local state
  const [schema, setSchema] = useState(Yup.object().shape({}));

  const moduleId = useAppSelector(
    (state) => state.simulatorHome.moduleNameToModuleIdMap.scenario_home
  );
  const [allowScenarioNameOverride, setAllowScenarioNameOverride] =
    useState(false);

  const [isDuplicateName, setIsDuplicateName] = useState(false);
  // Take existing checkbox value if duplication, else default True
  const [includeDemandTransference, setIncludeDemandTransference] = useState(isDuplicate && currentScenario ? !!currentScenario.is_dt_enabled : true)
  const [plannerIncluded, setPlannerIncluded] = useState(
    currentScenario?.planner_included ?? true
  );

  const {
    filters: filterFields,
    initialValues: filterInitialValues,
    combinations,
    ph_updated_at
  } = useAppSelector((state) => state.scenarios.scenarioFilters);
  const { fetchingScenarioFilters: isFetchingScenarioFilters, scenarioNames } =
    useAppSelector((state) => state.scenarios);
  const [isCreatingScenario, setIsCreatingScenario] = useState(false);
  const {
    user,
    user_name: userName,
    source
  } = useAppSelector((state) => state.user);

  const getName = () =>
    isEditable
      ? currentScenario.name
      : isDuplicate
        ? `${currentScenario.name} (Duplicate)`
        : `Copy of ${currentScenario.name}`;

  const formik = useFormik<IScenarioStaticFormFields>({
    initialValues: {
      name: currentScenario ? getName() : '',
      description: currentScenario?.description || '',
      user: currentScenario?.user || user,
      userName: currentScenario?.userName || userName,
      source: currentScenario?.source || source,
      date: Date.now()
    },
    validateOnChange: true,
    validationSchema: Yup.object({
      name: Yup.string()
        .required()
        .test({
          test: (scenarioName) => {
            setIsDuplicateName(false);
            if (allowScenarioNameOverride || isEditable) {
              return true;
            }
            if (scenarioName && currentScenario) {
              if (
                scenarioName.trim().toLowerCase() ===
                currentScenario.name.trim().toLowerCase()
              ) {
                setIsDuplicateName(true);
                setAllowScenarioNameOverride(false);
                return false;
              }
            }
            if (scenarioName) {
              return !scenarioNames.find(
                (scenario) => scenario.name === scenarioName.toLowerCase()
              );
            }
            return true;
          },
          message: isDuplicateName
            ? 'Can not create a scenario that is already loaded'
            : 'This name already exists.'
        })
    }),
    onSubmit(values) {
      console.log(values);
    }
  });

  /**
   * A function to get validation schema for formik
   * @param fields All fields
   * @param ppgToggle The current value of toggle (new or existing ppg)
   * @returns a Yup schema validation object for each of the field
   */
  const getValidationSchemaForFields = (fields: FormField2[]) =>
    fields
      .map((field) => {
        /**
         * passing 1st arguement as field &
         * 2nd arguement as showErrorMessage true or false i.e to show the error message or not on invalid form
         */
        const fieldSchema = getValidationSchema(field, false) as {
          [key: string]: Yup.StringSchema<string, AnyObject, string>;
        };
        return fieldSchema;
      })
      .reduce(
        (prev, curr) => ({ ...prev, ...curr }),
        {} as { [key: string]: any }
      );

  // If scenario is present then filter will present in the same object get the from that varibale
  const filterFormik = useFormik<IDynamicFilterFormFields>({
    initialValues: {},
    validationSchema: schema,
    onSubmit(values) {
      console.log(values);
    }
  });

  const getValueForSelection = (
    selectedFieldValue: ISelectOption[],
    presentFilterValue: any
  ) => selectedFieldValue.length === presentFilterValue.values.length;

  const getPayloadForScenario = () => ({
    ...formik.values,
    filter_data: Object.keys(filterFormik.values).map((filterName) => ({
      field_name: filterName,
      field_values: filterFormik.values[filterName].map((val) => val.value),
      field_alias:
        filterFields.find((field) => field.name === filterName)?.alias ||
        filterName,
      is_all_selected: getValueForSelection(
        filterFormik.values[filterName],
        filterFields.find((field) => field.name === filterName)
      )
    })),
    is_saveas: isDuplicate ? false : !!currentScenario,
    is_duplicated: isDuplicate,
    saveas_from: isDuplicate ? 0 : currentScenario?.pk_id || 0,
    duplicate_from: isDuplicate ? currentScenario?.pk_id : 0,
    allowScenarioNameOverride,
    include_demand_transference: includeDemandTransference,
    planner_included: plannerIncluded
  });
  const getPageName = () => {
    const page_title = window.location.href.split('/').at(-1);
    return page_title;
  };

  const createScenarioHandler = () => {
    // adding telemetry log for scenario create/update action
    trackEvent('ScenarioDialog_ScenarioActionClicked', {
      action: isEditable ? 'update' : (isDuplicate ? 'duplicate' : 'create'),
      scenarioId: currentScenario ? String(currentScenario.pk_id) : 'new',
      scenarioName: formik.values.name,
      includeDemandTransference,
      plannerIncluded,
      filterCount: Object.keys(filterFormik.values || {}).length,
      source: String(source || ''),
      moduleId: String(moduleId || '')
    });

    callFunction(
      MatomoEventTrackMapObject.track_create_scenario_button_click.event_key,
      getPageName()
    );
    setIsCreatingScenario(true);
    if (isEditable && currentScenario) {
      updateScenario(getPayloadForScenario()).then((response) => {
        // adding telemetry log for scenario update success
        trackEvent('ScenarioDialog_ScenarioUpdated', {
          scenarioId: String(currentScenario.pk_id),
          scenarioName: formik.values.name
        });
        setIsCreatingScenario(false);
        onClose(response.data);
      });
    } else if (currentScenario) {
      saveCopyOfScenario(getPayloadForScenario()).then((response) => {
        // adding telemetry log for scenario duplicate success
        trackEvent('ScenarioDialog_ScenarioDuplicated', {
          originalScenarioId: String(currentScenario.pk_id),
          newScenarioName: formik.values.name
        });
        setIsCreatingScenario(false);
        onClose(response.data);
      });
    } else {
      createScenario(getPayloadForScenario()).then((response) => {
        // adding telemetry log for scenario create success
        trackEvent('ScenarioDialog_ScenarioCreated', {
          scenarioName: formik.values.name,
          includeDemandTransference,
          plannerIncluded
        });
        setIsCreatingScenario(false);
        onClose(response.data);
      });
    }
  };

  //  function to set filter value on change
  const handleFilterSelectionChange = (
    fieldName: string,
    fieldValues: ISelectOption[]
  ) => {
    // adding telemetry log for filter selection change
    trackEvent('ScenarioDialog_FilterChanged', {
      fieldName,
      selectedValues: fieldValues.map(v => v.value).join(','),
      scenarioId: currentScenario ? String(currentScenario.pk_id) : 'new',
      moduleId: String(moduleId || '')
    });

    const changedFilterIndex = filterFields.findIndex(
      (filter) => filter.name === fieldName
    );
    const lastItemIndex = filterFields.length - 1;
    if (changedFilterIndex > -1 && changedFilterIndex !== lastItemIndex) {
      const precedingFilterValues = {
        ...filterFields.slice(0, changedFilterIndex).reduce<{
          [filterName: string]: ISelectOption[];
        }>(
          (previousValue, currentValue) => ({
            ...previousValue,
            ...{ [currentValue.name]: filterFormik.values[currentValue.name] }
          }),
          {}
        ),
        ...{
          [fieldName]: fieldValues
        }
      };
      precedingFilterValues[fieldName] = fieldValues;
      const newCombinations = combinations.filter((filterCombination) => {
        const keys = Object.keys(precedingFilterValues);
        return (
          keys.filter((key) =>
            precedingFilterValues[key]
              .map((val) => val.value)
              .includes(filterCombination[key] as string)
          ).length === keys.length
        );
      });
      const remainingFilterValues: {
        [filterName: string]: ISelectOption[];
      } = {};
      for (
        let index = changedFilterIndex + 1;
        index < filterFields.length;
        index++
      ) {
        const filterField = filterFields[index] as IMultiSelectControlField2;
        const dropdownOptionsForFilter = newCombinations
          .map<string>(
            (filterCombination) => filterCombination[filterField.name] as string
          )
          .filter((value) => !!value)
          .filter(
            (value, valueIndex, self) => self.indexOf(value) === valueIndex
          )
          .map<ISelectOption>((val) => ({
            label: val,
            value: val
          }));
        // filterField.values = dropdownOptionsForFilter.slice();
        remainingFilterValues[filterField.name] =
          dropdownOptionsForFilter.slice();
      }
      filterFormik.setValues({
        ...precedingFilterValues,
        ...remainingFilterValues
      });
      appDispatch(updateFilterFieldOptions(remainingFilterValues));
    }
    if (lastItemIndex === changedFilterIndex) {
      const previousValues = filterFormik.values;
      filterFormik.setValues({ ...previousValues, [fieldName]: fieldValues });
    }
  };

  // adding telemetry log for dialog open
  useEffect(() => {
    trackEvent('ScenarioDialog_Opened', {
      action: isEditable ? 'edit' : (isDuplicate ? 'duplicate' : 'create'),
      scenarioId: currentScenario ? String(currentScenario.pk_id) : 'new',
      source: String(source || ''),
      moduleId: String(moduleId || '')
    });

    // getting validation schema for the filters
    appDispatch(getScenarioFilters({ sourceId: source, moduleId })).then(
      (e) => {
        const { filters } = e.payload as { filters: FormField2[] };
        const validationSchema = getValidationSchemaForFields(filters);

        setSchema(Yup.object().shape(validationSchema));
      }
    );
    appDispatch(getScenarioNames(source));
    if (currentScenario) {
      formik.setValues({
        name: getName(),
        description: currentScenario?.description,
        user: currentScenario?.user,
        userName: currentScenario?.userName || userName,
        source: currentScenario?.source,
        date: Date.now()
      });
    }
  }, []);

  const getFilterValues = () => {
    const filterValues = JSON.parse(currentScenario.filter_data);
    let filterValueForScenario = {};
    filterValues.forEach((element) => {
      const filterDataMap = element.field_values.map((data) => ({
        label: data,
        value: data
      }));
      filterValueForScenario = Object.assign(filterValueForScenario, {
        [element.field_name]: filterDataMap
      });
    });
    return filterValueForScenario;
  };

  useEffect(() => {
    const filterValue = currentScenario ? getFilterValues() : {};
    if (currentScenario) {
      filterFormik.setValues(filterValue);
    } else if (filterFields.length) {
      filterFormik.setValues(filterInitialValues);
    }
  }, [filterInitialValues]);

  useEffect(() => {
    formik.validateField('name');
  }, [allowScenarioNameOverride]);

  useEffect(() => {
    if (isDuplicate && currentScenario) {
      setIncludeDemandTransference(!!currentScenario.is_dt_enabled);
    }
  }, [isDuplicate, currentScenario])

  useEffect(() => {
    if (currentScenario) {
      setPlannerIncluded(currentScenario.planner_included ?? true);
    } else {
      setPlannerIncluded(true)
    }
  }, [currentScenario]);

  // adding telemetry log for scenario name change
  const handleScenarioNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    trackEvent('ScenarioDialog_ScenarioNameChanged', {
      newName: e.target.value,
      scenarioId: currentScenario ? String(currentScenario.pk_id) : 'new',
      isDuplicateName: isDuplicateName,
      allowOverride: allowScenarioNameOverride
    });
    formik.handleChange(e);
  };

  // adding telemetry log for DT toggle change
  const handleDemandTransferenceToggle = (checked: boolean) => {
    trackEvent('ScenarioDialog_DemandTransferenceToggled', {
      previousValue: includeDemandTransference,
      newValue: checked,
      scenarioId: currentScenario ? String(currentScenario.pk_id) : 'new'
    });
    setIncludeDemandTransference(checked);
  };

  // adding telemetry log for planner module toggle
  const handlePlannerModuleToggle = (value: string) => {
    const newPlannerIncluded = value === 'with_planner';
    trackEvent('ScenarioDialog_PlannerModuleToggled', {
      previousValue: plannerIncluded,
      newValue: newPlannerIncluded,
      scenarioId: currentScenario ? String(currentScenario.pk_id) : 'new'
    });
    setPlannerIncluded(newPlannerIncluded);
  };

  // adding telemetry log for dialog close
  const handleDialogClose = () => {
    trackEvent('ScenarioDialog_Closed', {
      action: isEditable ? 'edit' : (isDuplicate ? 'duplicate' : 'create'),
      scenarioId: currentScenario ? String(currentScenario.pk_id) : 'new',
      wasCancelled: true
    });
    onClose(null);
    formik.resetForm();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleDialogClose}
    >
      <DialogContent sx={{ padding: 2 }}>
        <Grid container justifyContent='end'>
          <Grid item xs='auto'>
            <IconButton
              onClick={handleDialogClose}
            >
              <Close />
            </IconButton>
          </Grid>
        </Grid>
        <Grid container maxWidth='500px' spacing={1}>
          {isFetchingScenarioFilters ? (
            <LinearProgress sx={{ width: '200px' }} />
          ) : (
            <>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <FormLabel required htmlFor='name'>
                    Name
                  </FormLabel>
                  <StyledTextField
                    id='name'
                    size='small'
                    variant='outlined'
                    placeholder='Enter scenario name'
                    value={formik.values.name}
                    onChange={handleScenarioNameChange}
                    onBlur={formik.handleBlur}
                    disabled={isEditable}
                    error={formik.touched.name && !!formik.errors.name}
                  />
                  <StyledFormHelperText>
                    {formik.touched.name ? formik.errors.name || '' : ''}
                  </StyledFormHelperText>
                </FormControl>
              </Grid>
              <Grid
                container
                sx={{
                  display:
                    (formik.touched.name &&
                      formik.errors.name === 'This name already exists.') ||
                      allowScenarioNameOverride
                      ? 'flex'
                      : 'none'
                }}
                item
                xs={12}
                alignItems='center'
              >
                <Checkbox
                  checked={allowScenarioNameOverride}
                  onChange={(e) => {
                    // adding telemetry log for override checkbox toggle
                    trackEvent('ScenarioDialog_NameOverrideToggled', {
                      checked: e.target.checked,
                      scenarioId: currentScenario ? String(currentScenario.pk_id) : 'new'
                    });
                    setAllowScenarioNameOverride(e.target.checked);
                  }}
                />
                <Typography sx={{ fontSize: '10px' }} component='div'>
                  Do you still want to override?
                </Typography>
              </Grid>
              <Grid item xs={12} marginTop={1}>
                <FormControl fullWidth>
                  <FormLabel htmlFor='description'>
                    Scenario Objective{' '}
                    <Typography sx={{ fontSize: '10px' }} variant='caption'>
                      (Optional)
                    </Typography>
                  </FormLabel>
                  <StyledTextareaAutosize
                    id='description'
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    placeholder='Description of scenario'
                    minRows={3}
                    sx={{
                      resize: 'vertical',
                      fontSize: '10px',
                      maxHeight: '150px',
                      padding: '10px',
                      minHeight: '30px',
                      fontFamily:
                        'MarsCentra,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol",system-ui'
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} marginTop={1}>
                <Typography variant='text7' component='div'>
                  Details (Recommended to fill)
                </Typography>
              </Grid>
              <Grid container spacing={1} item xs={12}>
                {Object.keys(filterFormik.values || {}).length ? (
                  filterFields.map((control: IMultiSelectControlField2) => (
                    <Grid item xs={6} key={control.name}>
                      <SelectComponent
                        value={filterFormik.values[control.name]}
                        handleChange={(val) =>
                          handleFilterSelectionChange(control.name, val)
                        }
                        isDisabled={!!currentScenario}
                        label={control.alias}
                        options={control.values}
                        isMultiSelect
                      />
                    </Grid>
                  ))
                ) : (
                  <LinearProgress />
                )}
              </Grid>
              <Grid item xs={6} marginTop={1}>
                <FormControl fullWidth>
                  <FormLabel htmlFor='Date'>Date Created</FormLabel>
                  <StyledTextField
                    id='date'
                    disabled
                    size='small'
                    variant='outlined'
                    value={moment(formik.values.date).format('DD-MM-YYYY')}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6} marginTop={1}>
                <FormControl fullWidth>
                  <FormLabel htmlFor='author'>Author</FormLabel>
                  <StyledTextField
                    id='author'
                    disabled
                    size='small'
                    variant='outlined'
                    value={formik.values.userName}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6} marginTop={1}>
                <FormControl fullWidth>
                  <FormLabel htmlFor='author'>PH Recommendation Updated</FormLabel>
                  <StyledTextField
                    id='author'
                    disabled
                    size='small'
                    variant='outlined'
                    value={ph_updated_at['default_value']}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={6} marginTop={1}>
                <FormControl fullWidth>
                  <FormLabel sx={{ color: 'rgba(0,0,0,0.6)', fontWeight: 500 }}>
                    Include Demand Transference
                  </FormLabel>
                  <FormControlLabel
                    disabled={isEditable}
                    control={
                      <Checkbox
                        checked={includeDemandTransference}
                        onChange={(e) => handleDemandTransferenceToggle(e.target.checked)}
                        size="small"
                      />
                    }
                    label={<Typography sx={{ fontSize: '12px' }}>DT</Typography>}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} marginTop={1}>
                <FormControl fullWidth>
                  <FormLabel sx={{ color: 'rgba(0,0,0,0.6), fontWeight: 500', fontSize: '8px', marginBottom: '4px' }}>
                    Select modules to include
                  </FormLabel>
                  <RadioGroup
                    row
                    value={plannerIncluded ? 'with_planner' : 'simulator_only'}
                    onChange={(e) => handlePlannerModuleToggle(e.target.value)}
                    sx={{
                      gap: '12px'
                    }}
                  >
                    <FormControlLabel
                      value='with_planner'
                      disabled={isEditable}
                      control={<Radio size='small'
                        sx={{ padding: '3px' }} />}
                      sx={{ margin: 0 }}
                      label={
                        <Typography sx={{ fontSie: '8px' }}>
                          Simulator with Planner
                        </Typography>
                      }
                    />

                    <FormControlLabel
                      value='simulator_only'
                      disabled={isEditable}
                      control={<Radio size='small'
                        sx={{ padding: '3px' }} />}
                      sx={{ margin: 0 }}
                      label={
                        <Typography sx={{ fontSie: '8px' }}>
                          Simulator Only
                        </Typography>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </>
          )}

        </Grid>
      </DialogContent>
      <DialogActions sx={{ padding: 2 }}>
        <Grid container justifyContent='space-between'>
          <Grid xs='auto'>
            <Button
              onClick={handleDialogClose}
              variant='outlined'
              size='small'
            >
              Cancel
            </Button>
          </Grid>
          <Grid xs='auto'>
            <Button
              disabled={
                isCreatingScenario ||
                isFetchingScenarioFilters ||
                !formik.isValid ||
                !filterFormik.isValid
              }
              onClick={createScenarioHandler}
              color='primary'
              variant='contained'
              size='small'
              type='submit'
            >
              {isEditable ? 'Update & Continue' : 'Save & Continue'}
            </Button>
          </Grid>
        </Grid>
      </DialogActions>

      <Backdrop
        sx={{
          color: '#ffffff',
          zIndex: 9999,
          flexDirection: 'column',
          backdropFilter: 'blur(2px)'
        }}
        open={isCreatingScenario}>
        <CircularProgress color='primary' />
        <Typography sx={{
          mt: 3, px: 3,
          textAlign: 'center',
          color: 'text.primary',
          fontWeight: 500,
          fontSize: '12px'
        }}>
          {loaderText || "We're building your scenario based on your selection. It'll be ready shortly"}
        </Typography>
      </Backdrop>
    </Dialog>
  );
}